import { registerLocaleData } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import localeFr from "@angular/common/locales/fr";
import localeEn from "@angular/common/locales/en";
import localeEs from "@angular/common/locales/es";
import localePt from "@angular/common/locales/pt";
import {
  APP_INITIALIZER,
  ErrorHandler,
  LOCALE_ID,
  NgModule,
} from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltipDefaultOptions,
} from "@angular/material/tooltip";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { AuthService } from "./features/auth/services/auth.service";
import { AuthInterceptor } from "./interceptors/auth.interceptor";
import { DateHttpInterceptor } from "./interceptors/date.interceptor";
import { ErrorInterceptor } from "./interceptors/error.interceptor";
import { SpinInterceptor } from "./interceptors/spin.interceptor";
import { TutorialSandboxInterceptor } from "./shared/components/tutorial/tutorial-sandbox.interceptor";
import { SpecMutationsInterceptor } from "./interceptors/spec-mutations.interceptor";
import { GeneralSocketService } from "./shared/services/general-socket.service";
import { SharedModule } from "./shared/shared.module";
import { Router, RouteReuseStrategy } from "@angular/router";
import * as Sentry from "@sentry/angular-ivy";
import { SentryService } from "./features/logs/services/sentry.service";
import { RouteGuard } from "./features/auth/guards/route.guard";
import { OffreGuard } from "./features/auth/guards/offre.guard";
import { TitleComponent } from "./shared/components/title/title.component";
import { JustLogoComponent } from "./shared/components/just-logo/just-logo.component";
import { NgStyle } from "@angular/common";
import { environment } from "../environments/environment";
import { buildVersion } from "../build-version";
import { DefaultNoAutocompleteDirective } from "./shared/directives/default-no-autocomplete.directive";
import { CustomReuseStrategy } from "./shared/guards/custom-route-strategy";
import { GlobalErrorHandler } from "./features/logs/services/global-error-handler.service";
import { MAT_DATE_LOCALE } from "@angular/material/core";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { CustomPaginatorIntl, LanguageService } from "./shared/services/language.service";

//import { MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
//import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
/*
export function momentAdapterFactory() {
  return adapterFactory(moment);
};*/

registerLocaleData(localeFr, "fr");
registerLocaleData(localeEn, "en");
registerLocaleData(localeEs, "es");
registerLocaleData(localePt, "pt");

/*
export function loggerCallback(logLevel: LogLevel, message: string) {
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: '8993c8fc-8a8e-43dc-8fdf-5e31b42219ff',
      authority: 'https://login.microsoftonline.com/consumers', // Prod environment. Uncomment to use.
      redirectUri: 'https://localhost:4200/',
      postLogoutRedirectUri: '/'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']); // Prod environment. Uncomment to use.
  //protectedResourceMap.set('https://graph.microsoft-ppe.com/v1.0/me', ['user.read']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    },
    loginFailedRoute: '/login-failed'
  };
}
*/

@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    TitleComponent,
    JustLogoComponent,
    NgStyle,
  ],
  providers: [
    // ── Tooltips Material ─────────────────────────────────────────────────
    // Délai d'affichage par défaut pour tous les `matTooltip` de l'app.
    // Évite les clignotements lors d'un survol rapide sur des grilles
    // (ex: tableau onglet Marge cost-impact, widgets dashboard).
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 400,
        hideDelay: 100,
        touchendHideDelay: 1500,
        position: 'below',
      } as MatTooltipDefaultOptions,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      // Le dictionnaire de la langue stockée doit être EN PLACE avant le
      // premier rendu. Sans cette attente, les écrans s'affichaient avec le
      // français (seul dictionnaire du bundle initial) et le chunk EN/ES/PT
      // arrivait après : le pipe `trans` étant *pure*, rien ne réaffichait, et
      // choisir English laissait toute l'application en français.
      provide: APP_INITIALIZER,
      useFactory: (langue: LanguageService) => () => langue.pret,
      deps: [LanguageService],
      multi: true,
    },
    {
      // Force la création d'AuthService au tout début du boot, AVANT le
      // router et les interceptors. Le constructor d'AuthService hydrate
      // les signaux d'auth depuis localStorage de façon synchrone — sans
      // ce APP_INITIALIZER, AuthService pouvait être instancié trop tard
      // (après la 1ère requête HTTP), causant un faux soft-logout sur les
      // fenêtres détachées (window.open du widget supervision / fiche OF).
      provide: APP_INITIALIZER,
      useFactory: (_auth: AuthService) => () => Promise.resolve(),
      deps: [AuthService],
      multi: true,
    },
    RouteGuard,
    OffreGuard,
    GeneralSocketService,
    { provide: LOCALE_ID, useFactory: () => LanguageService.getLocaleId() },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DateHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinInterceptor,
      multi: true,
    } /*
        {
          provide: MSAL_INSTANCE,
          useFactory: MSALInstanceFactory
        },
        {
          provide: MSAL_GUARD_CONFIG,
          useFactory: MSALGuardConfigFactory
        },
        {
          provide: MSAL_INTERCEPTOR_CONFIG,
          useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,*/,
    { provide: HTTP_INTERCEPTORS, useClass: TutorialSandboxInterceptor, multi: true },
    // Signale les écritures de spécification aux vues qui en dérivent (compteur
    // « Modifications », diff de versions) — voir SpecMutationsInterceptor.
    { provide: HTTP_INTERCEPTORS, useClass: SpecMutationsInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    { provide: MAT_DATE_LOCALE, useFactory: () => LanguageService.getLocaleId() },
    { provide: MatPaginatorIntl, useExisting: CustomPaginatorIntl },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(sentryService: SentryService) {
    console.log(
      "version:",
      `${environment.version}.${buildVersion.buildNumber}.${buildVersion.commit}`,
    );
    sentryService.initSentry();
  }
}
